import { useState, useRef } from "react";
import { Upload, Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { API, CONTACT } from "../lib/constants";

const SOLUTIONS = [
  { value: "privacy_smart_film", label: "Privacy Smart Film" },
  { value: "led_display_film", label: "LED Display Film" },
  { value: "smart_wall_panels", label: "Smart Wall Panels" },
  { value: "unknown", label: "Ik weet het nog niet" },
];

export default function LeadForm() {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    naam: "",
    email: "",
    telefoon: "",
    gemeente: "",
    klant_type: "Particulier",
    oplossing: "privacy_smart_film",
    afmetingen: "",
    bericht: "",
  });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.email || !form.telefoon || !form.gemeente) {
      toast.error("Vul alle verplichte velden in.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      const res = await fetch(`${API}/leads`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Er ging iets mis");
      }
      setSuccess(true);
      toast.success("Aanvraag ontvangen!");
      setForm({
        naam: "",
        email: "",
        telefoon: "",
        gemeente: "",
        klant_type: "Particulier",
        oplossing: "privacy_smart_film",
        afmetingen: "",
        bericht: "",
      });
      setPhoto(null);
    } catch (e2) {
      toast.error(e2.message || "Er ging iets mis, probeer opnieuw");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lead-form"
      className="relative py-24 md:py-32"
      data-testid="lead-form-section"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-cyan-400 mb-4">
            Gratis intake
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight">
            Vraag gratis prijsinschatting aan.
          </h2>
          <p className="mt-5 text-base md:text-lg text-zinc-400 font-body">
            Vertel ons over je project. We nemen binnen enkele werkdagen contact
            op met een duidelijke inschatting.
          </p>
        </div>

        {success ? (
          <div
            className="jn-glass rounded-3xl p-10 md:p-14 text-center"
            data-testid="lead-form-success"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center mb-6">
              <Check className="text-cyan-400" size={26} />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-white font-medium">
              Bedankt!
            </h3>
            <p className="mt-3 text-zinc-400 font-body max-w-md mx-auto">
              We nemen snel contact met je op voor een eerste inschatting.
            </p>
            <button
              onClick={() => setSuccess(false)}
              className="mt-8 jn-btn-ghost px-6 py-3 rounded-full text-sm"
              data-testid="lead-form-new"
            >
              Nieuwe aanvraag indienen
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="jn-glass rounded-3xl p-7 md:p-10 grid md:grid-cols-2 gap-5 md:gap-6"
            data-testid="lead-form"
          >
            <Field label="Naam" required>
              <Input
                value={form.naam}
                onChange={onChange("naam")}
                required
                placeholder="Voor- en achternaam"
                className="jn-input"
                data-testid="input-naam"
              />
            </Field>
            <Field label="E-mail" required>
              <Input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                required
                placeholder="jij@voorbeeld.be"
                className="jn-input"
                data-testid="input-email"
              />
            </Field>
            <Field label="Telefoon" required>
              <Input
                value={form.telefoon}
                onChange={onChange("telefoon")}
                required
                placeholder="+32 ..."
                className="jn-input"
                data-testid="input-telefoon"
              />
            </Field>
            <Field label="Gemeente" required>
              <Input
                value={form.gemeente}
                onChange={onChange("gemeente")}
                required
                placeholder="Bv. Antwerpen"
                className="jn-input"
                data-testid="input-gemeente"
              />
            </Field>

            <Field label="Type klant">
              <Select
                value={form.klant_type}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, klant_type: v }))
                }
              >
                <SelectTrigger
                  className="jn-input h-11"
                  data-testid="select-klant-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  <SelectItem value="Particulier">Particulier</SelectItem>
                  <SelectItem value="Bedrijf">Bedrijf</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Gewenste oplossing">
              <Select
                value={form.oplossing}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, oplossing: v }))
                }
              >
                <SelectTrigger
                  className="jn-input h-11"
                  data-testid="select-oplossing"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  {SOLUTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Geschatte afmetingen" full>
              <Input
                value={form.afmetingen}
                onChange={onChange("afmetingen")}
                placeholder="Bv. 2 ramen van 1,2 x 1,8 m"
                className="jn-input"
                data-testid="input-afmetingen"
              />
            </Field>

            <Field label="Bericht" full>
              <Textarea
                value={form.bericht}
                onChange={onChange("bericht")}
                rows={5}
                placeholder="Vertel kort over je project of ruimte..."
                className="jn-input resize-none"
                data-testid="input-bericht"
              />
            </Field>

            <Field label="Foto van ruimte (optioneel)" full>
              <div
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-3 border border-dashed border-white/15 hover:border-cyan-400/60 rounded-xl px-5 py-4 cursor-pointer transition"
                data-testid="file-upload-zone"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center border border-white/10"
                  style={{ background: "rgba(255,255,255,0.04)" }}
                >
                  <Upload size={16} className="text-cyan-400" />
                </div>
                <div className="flex-1 text-sm font-body">
                  {photo ? (
                    <span className="text-white">{photo.name}</span>
                  ) : (
                    <>
                      <span className="text-white">Klik om te uploaden</span>{" "}
                      <span className="text-zinc-500">
                        of sleep een foto hier (jpg, png, webp)
                      </span>
                    </>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  data-testid="file-input"
                />
              </div>
              <p className="mt-3 text-xs text-zinc-500 font-body">
                Liever snel?{" "}
                <a
                  href={`https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(CONTACT.whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:underline"
                  data-testid="leadform-whatsapp-hint"
                >
                  Stuur ons foto's van je raam of glaswand via WhatsApp.
                </a>
              </p>
            </Field>

            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 pt-3">
              <p className="text-xs text-zinc-500 font-body">
                Door te verzenden bevestig je dat we contact mogen opnemen over
                je aanvraag.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="jn-btn-primary inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm md:text-base disabled:opacity-60"
                data-testid="lead-form-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Bezig...
                  </>
                ) : (
                  "Verstuur aanvraag"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
      <style>{`
        .jn-input {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
        }
        .jn-input:focus, .jn-input:focus-visible {
          border-color: rgba(0,229,255,0.6) !important;
          box-shadow: 0 0 0 3px rgba(0,229,255,0.15) !important;
          outline: none;
        }
        .jn-input::placeholder { color: #52525b; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children, full }) {
  return (
    <div className={full ? "md:col-span-2 flex flex-col gap-2" : "flex flex-col gap-2"}>
      <Label className="text-xs uppercase tracking-[0.18em] text-zinc-400 font-body">
        {label} {required && <span className="text-cyan-400">*</span>}
      </Label>
      {children}
    </div>
  );
}
